//# sourceMappingURL=redraw.mjs.map
/// <reference types="./redraw.d.mts" />
import * as $react from "../../redraw/redraw.mjs";
import * as $a from "../../redraw_dom/redraw/dom/attribute.mjs";
import * as $html from "../../redraw_dom/redraw/dom/html.mjs";
import * as $sketch from "../../sketch/sketch.mjs";
import * as $css from "../../sketch/sketch/css.mjs";
import { Ok, prepend as listPrepend, CustomType as $CustomType, makeError } from "../gleam.mjs";
import * as $mutable from "../sketch/redraw/internals/mutable.mjs";
import * as $props from "../sketch/redraw/internals/props.mjs";
import * as $styles from "../sketch/redraw/internals/styles.mjs";
import { context, get as get_context } from "./redraw.ffi.mjs";

const FILEPATH = "src/sketch/redraw.gleam";

class Cache extends $CustomType {
  constructor(stylesheet, context) {
    super();
    this.stylesheet = stylesheet;
    this.context = context;
  }
}

class StyleSheet extends $CustomType {
  constructor(cache, render) {
    super();
    this.cache = cache;
    this.render = render;
  }
}

/**
 * Error message used when querying context. Should be used to indicate to the
 * user what should be done before using `sketch_redraw`.
 * 
 * @ignore
 */
const error_msg = "Sketch Redraw Provider not set. Please, add the provider in your render tree.";

/**
 * Creates the Sketch Context and initialises the Sketch cache. Use it in
 * conjuction with [`provider`](#provider) to setup Sketch Redraw, otherwise
 * nothing will work.
 *
 * ```gleam
 * import redraw
 * import redraw/dom/client
 * import sketch/redraw as sketch_redraw
 *
 * pub fn main() {
 *   let app = app()
 *   let cache = sketch_redraw.create_cache()
 *   let root = client.create_root("root")
 *   client.render(root, {
 *     redraw.strict_mode([
 *       sketch_redraw.provider(cache, [
 *         app(),
 *       ]),
 *     ])
 *   })
 * }
 * ```
 */
export function create_cache() {
  let style = $styles.create_node();
  let $ = $sketch.stylesheet(new $sketch.Persistent());
  let cache;
  if ($ instanceof Ok) {
    cache = $[0];
  } else {
    throw makeError(
      "let_assert",
      FILEPATH,
      "sketch/redraw",
      51,
      "create_cache",
      "Pattern match failed, no pattern matched the value.",
      {
        value: $,
        start: 1649,
        end: 1718,
        pattern_start: 1660,
        pattern_end: 1669
      }
    )
  }
  let cache$1 = $mutable.from(cache);
  let render = () => {
    return $styles.dump(style, $sketch.render($mutable.get(cache$1)));
  };
  let stylesheet = new StyleSheet(cache$1, render);
  let context$1 = context(stylesheet);
  return new Cache(stylesheet, context$1);
}

/**
 * Creates the Sketch Context and initialises the Sketch cache. Use it in
 * conjuction with [`provider`](#provider) to setup Sketch Redraw, otherwise
 * nothing will work. `initialise_cache` acts like `create_cache`, but provides
 * a customisation function to modify the stylesheet at initialisation (to
 * inject custom styles, keyframes, etc.).
 *
 * ```gleam
 * import redraw
 * import redraw/dom/client
 * import sketch/redraw as sketch_redraw
 *
 * pub fn main() {
 *   let app = app()
 *   let cache = initialise_cache()
 *   let root = client.create_root("root")
 *   client.render(root, {
 *     redraw.strict_mode([
 *       sketch_redraw.provider(cache, [
 *         app(),
 *       ]),
 *     ])
 *   })
 * }
 *
 * pub fn initialise_cache() {
 *   use stylesheet <- sketch_redraw.initialise_cache()
 *   stylesheet
 *   |> sketch.at_rule(keyframes.wave(), _)
 *   |> sketch.at_rule(keyframes.pulse(), _)
 * }
 * ```
 */
export function initialise_cache(prepare) {
  let style = $styles.create_node();
  let $ = $sketch.stylesheet(new $sketch.Persistent());
  let cache;
  if ($ instanceof Ok) {
    cache = $[0];
  } else {
    throw makeError(
      "let_assert",
      FILEPATH,
      "sketch/redraw",
      94,
      "initialise_cache",
      "Pattern match failed, no pattern matched the value.",
      {
        value: $,
        start: 3038,
        end: 3107,
        pattern_start: 3049,
        pattern_end: 3058
      }
    )
  }
  let cache$1 = prepare(cache);
  let cache$2 = $mutable.from(cache$1);
  let render = () => {
    return $styles.dump(style, $sketch.render($mutable.get(cache$2)));
  };
  let stylesheet = new StyleSheet(cache$2, render);
  let context$1 = context(stylesheet);
  return new Cache(stylesheet, context$1);
}

/**
 * Create the Sketch provider used to manage the `StyleSheet`. \
 * This makes sure identical styles will never be computed twice. \
 * Use it at root of your render function.
 *
 * ```gleam
 * import redraw
 * import redraw/dom/client
 * import sketch/redraw as sketch_redraw
 *
 * pub fn main() {
 *   let app = app()
 *   let cache = sketch_redraw.create_cache()
 *   let root = client.create_root("root")
 *   client.render(root, {
 *     redraw.strict_mode([
 *       sketch_redraw.provider(cache, [
 *         app(),
 *       ]),
 *     ])
 *   })
 * }
 * ```
 */
export function provider(setup, children) {
  let stylesheet = setup.stylesheet;
  let context$1 = setup.context;
  return $react.provider(context$1, stylesheet, children);
}

function use_render(stylesheet, class_name) {
  return ((_capture) => {
    return $react.use_insertion_effect(_capture, [class_name]);
  })(() => { return stylesheet.render(); });
}

function use_styles(cache, styles) {
  return ((_capture) => {
    return $react.use_memo(_capture, [cache, styles.as_string]);
  })(
    () => {
      let stylesheet = $mutable.get(cache);
      let $ = $sketch.class_name(styles, stylesheet);
      let cache_ = $[0];
      let class_name = $[1];
      $mutable.set(cache, cache_);
      return class_name;
    },
  );
}

function use_sketch_context() {
  let $ = get_context();
  if ($ instanceof Ok) {
    let context$1 = $[0];
    return $react.use_context(context$1);
  } else {
    throw makeError(
      "panic",
      FILEPATH,
      "sketch/redraw",
      244,
      "use_sketch_context",
      error_msg,
      {}
    )
  }
}

/**
 * Generates the corresponding class name to `styles`.
 *
 * ```gleam
 * import redraw
 * import redraw/dom/attribute
 * import redraw/dom/client
 * import redraw/dom/html
 * import sketch/redraw as sketch_redraw
 *
 * pub fn my_component() {
 *   use <- redraw.standalone("MyComponent")
 *   let class_name = sketch_redraw.use_class_name(my_class())
 *   html.div([attribute.class_name(class_name)], [
 *     html.text("Styled!"),
 *   ])
 * }
 * ```
 */
export function use_class_name(styles) {
  let stylesheet = use_sketch_context();
  let class_name = use_styles(stylesheet.cache, styles);
  use_render(stylesheet, class_name);
  return $react.use_memo(() => { return class_name; }, [class_name]);
}

/**
 * React Component rendering a styled HTML element. Those components are
 * created lazily on-demand, and are named correctly according to the `tag`.
 * 
 * @ignore
 */
function factory(props) {
  let $ = $styles.extract_from(props);
  let tag = $[0];
  let styles = $[1];
  let props$1 = $[2];
  let class_name = use_class_name(styles);
  let props$2 = $props.append(props$1, "className", class_name);
  return $react.jsx(tag, props$2, undefined, false);
}

/**
 * Style a native DOM node. `styled` creates an intermediate component named
 * `Sketch.Styled(tag)` which will render the styles in the StyleSheet injected
 * in Context, and inject the class name directly on the node. Every other
 * props are kept as-is.
 *
 * ```gleam
 * import redraw.{type Component}
 * import redraw/dom/attribute.{type Attribute}
 * import sketch/css.{type Class}
 *
 * pub fn my_node(
 *   styles: Class,
 *   props: List(Attribute),
 *   children: List(Component),
 * ) -> Component {
 *   styled("my_node", styles, props, children)
 * }
 * ```
 * 
 * @ignore
 */
export function styled(tag, styles, props, children) {
  let as_ = $a.attribute("as", tag);
  let styles$1 = $a.attribute("styles", styles);
  let fun = $styles.cache(tag, factory);
  let props$1 = $html.to_props(listPrepend(as_, listPrepend(styles$1, props)));
  return $react.jsx(fun, props$1, children, true);
}

/**
 * React Component rendering a styled HTML element. Those components are
 * created lazily on-demand, and are named correctly according to the `tag`.
 * 
 * @ignore
 */
function hook_factory(props) {
  let $ = $styles.hook_extract_from(props);
  let tag = $[0];
  let use_user_styles = $[1];
  let props$1 = $[2];
  let styles = use_user_styles();
  let class_name = use_class_name(styles);
  let props$2 = $props.append(props$1, "className", class_name);
  return $react.jsx(tag, props$2, undefined, false);
}

/**
 * Style a native DOM node. `hooked` creates an intermediate component named
 * `Sketch.Styled(tag)` which will render the styles in the StyleSheet injected
 * in Context, and inject the class name directly on the node. Every other
 * props are kept as-is.
 *
 * `styles` is a function that runs at top-level of the component, allowing
 * to use hooks directly in the function.
 *
 * ```gleam
 * import redraw.{type Component}
 * import redraw/dom/attribute.{type Attribute}
 * import sketch/css.{type Class}
 *
 * pub fn my_node(
 *   styles: Class,
 *   props: List(Attribute),
 *   children: List(Component),
 * ) -> Component {
 *   use <- hooked("my_node", props, children)
 *   redraw.use_memo(fn () { styles }, #(styles))
 * }
 * ```
 * 
 * @ignore
 */
export function hooked(tag, props, children, styles) {
  let as_ = $a.attribute("as", tag);
  let styles$1 = $a.attribute("styles", styles);
  let fun = $styles.hook_cache(tag, hook_factory);
  let props$1 = $html.to_props(listPrepend(as_, listPrepend(styles$1, props)));
  return $react.jsx(fun, props$1, children, true);
}
